import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/** Plain error panel for render-time failures. Query errors are handled per-page, not here. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  private reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
        <div className="w-full max-w-md rounded-lg border border-line bg-surface-pop p-6 shadow-pop">
          <h1 className="font-display text-headline text-ink">Something went wrong</h1>
          <p className="mt-2 text-body text-ink-2">
            {error.message || 'An unexpected error stopped this page from rendering.'}
          </p>
          <div className="mt-5 flex gap-2">
            <Button onClick={() => window.location.assign('/')}>Go home</Button>
            <Button variant="secondary" onClick={this.reset}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
